import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/motion/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CTASection from "@/components/CTASection";
import { Lock, ShieldCheck, KeyRound, ScrollText, ArrowUpRight } from "lucide-react";

const SERVER_IMG =
  "https://images.unsplash.com/photo-1695668548342-c0c1ad479aee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxjeWJlcnNlY3VyaXR5JTIwc2VjdXJlJTIwc2VydmVyJTIwbmV0d29ya3xlbnwwfHx8fDE3ODYxOTA4NTF8MA&ixlib=rb-4.1.0&q=85";

const secIcons = [KeyRound, ShieldCheck, Lock, ScrollText];

export default function Ecosystem() {
  const { t } = useLang();
  const p = t.portal;

  return (
    <div data-testid="ecosystem-page">
      <section className="relative pt-40 pb-16 overflow-hidden">
        <div
          className="absolute -top-20 right-10 w-[600px] h-[600px] pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,229,255,0.10), transparent 60%)" }}
        />
        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <SectionHeading eyebrow={p.heroEyebrow} title={p.heroTitle} sub={p.heroSub} />
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div
                className="relative overflow-hidden border border-cyan-precision/30"
                style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 10% 100%, 0 88%)" }}
              >
                <img src={SERVER_IMG} alt="Secure server room" className="w-full h-[380px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-widest text-cyan-precision">
                  <Lock size={13} /> Zero-Trust Infrastructure
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Systems */}
      <section className="py-24 lg:py-32 border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <SectionHeading title={p.systemsTitle} />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {p.systems.map((sys, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="group h-full border border-white/10 bg-navy-surface/40 p-8 lg:p-10 hover:border-cyan-precision/50 hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display text-2xl font-light text-[#F0F4F8]">{sys.n}</h4>
                    <ArrowUpRight
                      size={20}
                      className="text-[#94A3B8] group-hover:text-cyan-precision transition-colors"
                    />
                  </div>
                  {sys.sub && (
                    <div className="mt-2 font-mono-tech text-[12px] tracking-wide text-cyan-precision/80">
                      {sys.sub}
                    </div>
                  )}
                  <p className="mt-5 text-[#94A3B8] leading-relaxed">{sys.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Security layers */}
      <section className="py-24 lg:py-32 border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <SectionHeading title={p.securityTitle} />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {p.security.map((sec, i) => {
              const Icon = secIcons[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="h-full border border-white/10 bg-navy-surface/40 p-8 hover:border-cyan-precision/50 transition-colors duration-300">
                    <div className="w-11 h-11 border border-white/10 flex items-center justify-center text-cyan-precision">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <h4 className="mt-6 font-display text-lg text-[#F0F4F8]">{sec.t}</h4>
                    <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{sec.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection title={p.ctaTitle} body={p.ctaBody} btn={p.ctaBtn} />
    </div>
  );
}
